/**
 * Sticker Note Sprite Generator
 * Creates canvas-based sprites for MapLibre symbol layers
 */

import { logger } from './logger';

// Sticker colors
const STICKER_COLORS = {
  rose: '#E6B3C2',
  peach: '#F2C6A0',
  sage: '#C9D3B8',
  cream: '#F4EDE4',
  lavender: '#D4C4E0',
};

/**
 * Create a sticker note sprite with optional scribble lines
 * @param {string} color - Background color
 * @param {number} size - Size in pixels
 * @param {boolean} showScribbles - Whether to show fake text lines
 * @returns {HTMLCanvasElement}
 */
function createStickerSprite(color = STICKER_COLORS.rose, size = 64, showScribbles = true) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Add padding for shadow
  const padding = 8;
  canvas.width = size + padding * 2;
  canvas.height = size + padding * 2;

  const x = padding;
  const y = padding;

  // Shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 3;

  // Sticker background with slight rotation effect
  ctx.save();
  ctx.translate(x + size / 2, y + size / 2);
  ctx.rotate(-0.03); // Slight tilt
  ctx.translate(-(x + size / 2), -(y + size / 2));

  ctx.fillStyle = color;
  ctx.fillRect(x, y, size, size);

  // Reset shadow for details
  ctx.shadowColor = 'transparent';

  // Folded corner effect
  ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
  ctx.beginPath();
  ctx.moveTo(x + size - 10, y + size);
  ctx.lineTo(x + size, y + size - 10);
  ctx.lineTo(x + size, y + size);
  ctx.closePath();
  ctx.fill();

  // Scribble lines (fake text)
  if (showScribbles) {
    ctx.strokeStyle = 'rgba(74, 74, 74, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';

    const lineY = [y + 14, y + 24, y + 34, y + 44];
    const lineWidths = [0.7, 0.85, 0.6, 0.4]; // Varying lengths

    lineY.forEach((ly, i) => {
      const lineWidth = size * lineWidths[i];
      const startX = x + 8;

      ctx.beginPath();
      ctx.moveTo(startX, ly);
      // Wavy line to simulate handwriting
      const segments = 4;
      const segmentWidth = lineWidth / segments;
      for (let s = 0; s < segments; s++) {
        const sx = startX + s * segmentWidth;
        const ex = sx + segmentWidth;
        const cy = ly + (Math.random() - 0.5) * 2;
        ctx.quadraticCurveTo(sx + segmentWidth / 2, cy, ex, ly);
      }
      ctx.stroke();
    });
  }

  ctx.restore();

  return canvas;
}

/**
 * Create a cluster sticker (stacked notes effect)
 * @param {string} color - Dominant color
 * @param {number} count - Number of notes in cluster
 * @param {number} size - Size in pixels
 * @returns {HTMLCanvasElement}
 */
function createClusterSprite(color = STICKER_COLORS.rose, count = 5, size = 64) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const padding = 16;
  canvas.width = size + padding * 2;
  canvas.height = size + padding * 2;

  // Draw stacked notes (back to front)
  const stackCount = Math.min(count, 3);
  const offsets = [
    { x: 6, y: -4, rotation: 0.08 },
    { x: -4, y: -2, rotation: -0.05 },
    { x: 0, y: 0, rotation: -0.02 },
  ];

  for (let i = 0; i < stackCount; i++) {
    const offset = offsets[2 - (stackCount - 1 - i)];
    const x = padding + offset.x;
    const y = padding + offset.y;

    ctx.save();
    ctx.translate(x + size / 2, y + size / 2);
    ctx.rotate(offset.rotation);
    ctx.translate(-(x + size / 2), -(y + size / 2));

    // Shadow (only for back notes)
    if (i < stackCount - 1) {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 2;
    } else {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 3;
    }

    // Slightly different colors for stacked effect
    const alpha = i === stackCount - 1 ? 1 : 0.9;
    ctx.fillStyle = color;
    ctx.globalAlpha = alpha;
    ctx.fillRect(x, y, size, size);
    ctx.globalAlpha = 1;

    ctx.shadowColor = 'transparent';

    // Folded corner on top note
    if (i === stackCount - 1) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.beginPath();
      ctx.moveTo(x + size - 10, y + size);
      ctx.lineTo(x + size, y + size - 10);
      ctx.lineTo(x + size, y + size);
      ctx.closePath();
      ctx.fill();

      // Scribble lines on top note
      ctx.strokeStyle = 'rgba(74, 74, 74, 0.4)';
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';

      [y + 14, y + 24, y + 34].forEach((ly, idx) => {
        const lineWidth = size * [0.6, 0.75, 0.5][idx];
        ctx.beginPath();
        ctx.moveTo(x + 8, ly);
        ctx.lineTo(x + 8 + lineWidth, ly + (Math.random() - 0.5) * 2);
        ctx.stroke();
      });
    }

    ctx.restore();
  }

  return canvas;
}

/**
 * Create a beautiful sticker note sprite with enhanced aesthetics
 * @param {string} color - Background color
 * @param {number} size - Size in pixels
 * @param {boolean} showScribbles - Whether to show scribble lines (false for text overlay)
 */
function createSimpleSprite(color, size, showScribbles = true) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Extra padding for shadow and glow effects
  const padding = 10;
  canvas.width = size + padding * 2;
  canvas.height = size + padding * 2;

  const x = padding;
  const y = padding;

  // Soft ambient glow (like it's slightly lit)
  ctx.shadowColor = 'rgba(255, 245, 230, 0.4)';
  ctx.shadowBlur = 12;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  // Main square with slight rotation for organic feel
  ctx.save();
  ctx.translate(x + size / 2, y + size / 2);
  ctx.rotate(-0.025 + Math.random() * 0.01); // Slight random tilt for variety
  ctx.translate(-(x + size / 2), -(y + size / 2));

  // Drop shadow (separate from glow)
  ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 4;

  // Rounded corners for softer look
  const radius = 4;
  ctx.beginPath();
  ctx.roundRect(x, y, size, size, radius);
  ctx.fillStyle = color;
  ctx.fill();

  // Reset shadow for details
  ctx.shadowColor = 'transparent';

  // Multi-layer gradient for paper-like depth
  const gradient = ctx.createLinearGradient(x, y, x + size, y + size);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
  gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.08)');
  gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.08)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.roundRect(x, y, size, size, radius);
  ctx.fill();

  // Subtle inner highlight at top edge (paper shine)
  const topHighlight = ctx.createLinearGradient(x, y, x, y + size * 0.15);
  topHighlight.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
  topHighlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = topHighlight;
  ctx.beginPath();
  ctx.roundRect(x + 2, y + 1, size - 4, size * 0.12, [radius, radius, 0, 0]);
  ctx.fill();

  // Folded corner effect with paper texture
  const foldSize = size * 0.16;

  // Shadow under fold
  ctx.fillStyle = 'rgba(0, 0, 0, 0.12)';
  ctx.beginPath();
  ctx.moveTo(x + size - foldSize - 2, y + size);
  ctx.lineTo(x + size, y + size - foldSize - 2);
  ctx.lineTo(x + size, y + size);
  ctx.closePath();
  ctx.fill();

  // Fold itself (lighter)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
  ctx.beginPath();
  ctx.moveTo(x + size - foldSize, y + size);
  ctx.lineTo(x + size, y + size - foldSize);
  ctx.lineTo(x + size, y + size);
  ctx.closePath();
  ctx.fill();

  // Fold crease line
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(x + size - foldSize, y + size);
  ctx.lineTo(x + size, y + size - foldSize);
  ctx.stroke();

  // Fold highlight edge
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + size - foldSize + 1, y + size - 1);
  ctx.lineTo(x + size - 1, y + size - foldSize + 1);
  ctx.stroke();

  // Calligraphic scribble lines (only if showScribbles is true)
  if (showScribbles) {
    ctx.strokeStyle = 'rgba(90, 70, 52, 0.3)'; // Warmer brown tone
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const margin = size * 0.16;
    const lineSpacing = size * 0.13;
    const lines = [
      { width: 0.65, thickness: 1.8 },
      { width: 0.8, thickness: 1.5 },
      { width: 0.5, thickness: 1.3 },
      { width: 0.35, thickness: 1.1 },
    ];

    lines.forEach((line, i) => {
      const lineY = y + margin + 2 + i * lineSpacing;
      const lineWidth = (size - margin * 2) * line.width;
      ctx.lineWidth = line.thickness;

      // Create wavy calligraphic line with pen pressure variation
      ctx.beginPath();
      ctx.moveTo(x + margin, lineY);

      const segments = 6;
      const segmentWidth = lineWidth / segments;
      for (let s = 0; s < segments; s++) {
        const sx = x + margin + s * segmentWidth;
        const ex = sx + segmentWidth;
        // More natural wave pattern
        const wave = Math.sin(s * 1.8 + i * 0.7) * 1.2;
        const cp1y = lineY + wave;
        const cp2y = lineY - wave * 0.6;
        ctx.bezierCurveTo(
          sx + segmentWidth * 0.35, cp1y,
          sx + segmentWidth * 0.65, cp2y,
          ex, lineY + (Math.sin((s + 1) * 1.5 + i) * 0.6)
        );
      }
      ctx.stroke();
    });
  }

  // Subtle paper texture (noise effect)
  ctx.globalAlpha = 0.03;
  for (let i = 0; i < 50; i++) {
    const tx = x + Math.random() * size;
    const ty = y + Math.random() * size;
    ctx.fillStyle = Math.random() > 0.5 ? '#000' : '#fff';
    ctx.fillRect(tx, ty, 1, 1);
  }
  ctx.globalAlpha = 1;

  ctx.restore();
  return canvas;
}

/**
 * Register all sticker sprites with the map
 * @param {maplibregl.Map} map
 */
export function registerStickerSprites(map) {
  try {
    // Create sprites for all colors - unified size for smooth transitions
    const colors = ['rose', 'peach', 'sage', 'lavender', 'cream'];

    // Single optimized size for all zoom levels
    const sizes = [
      { suffix: 'medium', size: 80, scribbles: false },  // Clean for text overlay at high zoom
    ];

    const spriteConfigs = [];

    // Add sticker sprites for all color/size combinations
    colors.forEach(colorName => {
      sizes.forEach(({ suffix, size, scribbles }) => {
        spriteConfigs.push({
          name: `sticker-${colorName}-${suffix}`,
          color: STICKER_COLORS[colorName],
          size,
          scribbles
        });
      });
      // Also add cluster variant (with scribbles for low zoom)
      spriteConfigs.push({
        name: `cluster-${colorName}`,
        color: STICKER_COLORS[colorName],
        size: 72,
        scribbles: true
      });
    });

    spriteConfigs.forEach(({ name, color, size, scribbles }) => {
      if (!map.hasImage(name)) {
        const canvas = createSimpleSprite(color, size, scribbles);

        // Convert canvas to ImageData for MapLibre
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        map.addImage(name, {
          width: canvas.width,
          height: canvas.height,
          data: imageData.data
        });

        logger.debug(`✅ Added sprite: ${name} (${canvas.width}x${canvas.height})`);
      }
    });

    // Verify
    logger.debug('🎨 Sprites registered. Has cluster-rose:', map.hasImage('cluster-rose'));

  } catch (err) {
    logger.error('❌ Error registering sprites:', err);
  }
}

/**
 * Get sprite name from color hex
 * @param {string} colorHex
 * @returns {string}
 */
export function getSpriteNameFromColor(colorHex) {
  const colorMap = {
    '#E6B3C2': 'rose',
    '#F2C6A0': 'peach',
    '#C9D3B8': 'sage',
    '#F4EDE4': 'cream',
    '#D4C4E0': 'lavender',
  };
  return colorMap[colorHex?.toUpperCase()] || 'rose';
}

export { STICKER_COLORS, createStickerSprite, createClusterSprite, createSimpleSprite };
